// =============================================================================
// Infrastructure — super-admin /admin/infrastructure
//
// Single pane for the operator to see:
//   • Edge functions that are scheduled via pg_cron (on either project)
//   • Vault secret metadata (names + timestamps), with create / rotate /
//     unset actions for ops who don't want to touch the SQL editor
//   • Edge-function environment secret NAMES (read-only; values stay
//     behind the Supabase dashboard)
//   • An audit-log feed of every change made from this page
//
// All read + write paths go through two Platform edge functions
// (infra-list, infra-mutate), which gate on platform_admins membership
// and proxy SQL via the Supabase Management API. The page itself just
// renders state; all rules / secrets / sanitisation live server-side.
// =============================================================================

import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Server, Database, KeyRound, RefreshCw, Trash2, Plus, AlertTriangle,
  CheckCircle2, XCircle, Clock, Power, Loader2, Eye, EyeOff,
  ScrollText, ChevronRight, History, Play, X, Zap,
} from 'lucide-react';
// Mounted as the first tab on this page. EdgeFunctionsCatalogTab is the
// inner content only (no page header / chrome) — Infrastructure provides
// the outer h1 + container + tab nav.
import { EdgeFunctionsCatalogTab } from './EdgeFunctions';
import { toast } from 'sonner';
import { cn } from '@/utils/helpers';
import {
  listScheduledFunctions,
  listVaultSecrets,
  listEdgeSecrets,
  toggleCronJob,
  listJobRuns,
  runCronNow,
  createVaultSecret,
  rotateVaultSecret,
  unsetVaultSecret,
  setEdgeSecret,
  unsetEdgeSecret,
  fetchInfraAuditLog,
  type InfraTarget,
  type InfraScheduledFunctionRow,
  type InfraJobRunRow,
  type InfraVaultSecretRow,
  type InfraEdgeSecretRow,
  type InfraAuditRow,
} from '@/lib/api';

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Translate a 5-field cron expression into something the operator
 *  can read. Falls back to the raw expression if we don't recognise
 *  the pattern — better to show the truth than guess wrong. */
function humanCron(expr: string): string {
  const e = expr.trim();
  if (e === '* * * * *')   return 'every minute';
  if (e === '*/5 * * * *') return 'every 5 minutes';
  if (e === '*/10 * * * *') return 'every 10 minutes';
  if (e === '*/15 * * * *') return 'every 15 minutes';
  if (e === '*/30 * * * *') return 'every 30 minutes';
  if (e === '0 * * * *')   return 'hourly';
  if (/^0 \d{1,2} \* \* \*$/.test(e)) {
    const [, hr] = e.match(/^0 (\d{1,2}) \* \* \*$/)!;
    return `daily at ${hr.padStart(2,'0')}:00 UTC`;
  }
  if (/^0 \d{1,2} \* \* \d$/.test(e)) {
    const [, hr, dow] = e.match(/^0 (\d{1,2}) \* \* (\d)$/)!;
    const day = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][Number(dow)] ?? `day ${dow}`;
    return `weekly · ${day} ${hr.padStart(2,'0')}:00 UTC`;
  }
  return e;
}

/** Best-effort "X ago" formatter for ISO timestamps. */
function timeAgo(iso: string | null): string {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms) || ms < 0) return '—';
  const s = Math.floor(ms / 1000);
  if (s < 60)      return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60)      return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 48)      return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30)      return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

/** Heuristic: which Vault / edge-function secret names appear inside
 *  this cron command body? Regex over the SQL — purely an aid; the
 *  "auto-detected" pill in the UI flags the inference for the user. */
function detectSecretRefs(command: string): string[] {
  const refs = new Set<string>();
  // vault.decrypted_secrets WHERE name = 'X'
  const vaultRe = /vault\.decrypted_secrets[^']*name\s*=\s*'([^']+)'/gi;
  let m: RegExpExecArray | null;
  while ((m = vaultRe.exec(command)) !== null) refs.add(m[1]);
  // current_setting('app.<X>') — older GUC pattern
  const gucRe = /current_setting\(\s*'([a-z0-9_.]+)'\s*\)/gi;
  while ((m = gucRe.exec(command)) !== null) refs.add(m[1]);
  // x-<thing>-secret headers — useful when the secret is interpolated
  const headerRe = /'(x-[a-z0-9-]*secret)'/gi;
  while ((m = headerRe.exec(command)) !== null) refs.add(m[1]);
  return Array.from(refs);
}

// ── Modal scaffolding ─────────────────────────────────────────────────────────

function Modal({
  open, onClose, title, children,
}: {
  open:    boolean;
  onClose: () => void;
  title:   string;
  children: React.ReactNode;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div
        className="w-full max-w-md rounded-2xl border border-overlay/[0.08] bg-surface-0 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-overlay/[0.06]">
          <h3 className="text-base font-semibold text-surface-900">{title}</h3>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

// ── Job Run History modal ────────────────────────────────────────────────────
//
// Wider than the generic `Modal` because it renders a table with
// timestamp + duration + status + message columns. Lazy-loads on
// open via listJobRuns; surfaces server-side ok/error states the
// same way the parent tab does, so an operator never sees a half-
// loaded modal with stale rows from a previous job.

function JobRunsModal({
  open,
  onClose,
  target,
  row,
}: {
  open:     boolean;
  onClose:  () => void;
  target:   InfraTarget;
  row:      InfraScheduledFunctionRow | null;
}) {
  const [runs, setRuns]       = useState<InfraJobRunRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);

  // Reload whenever the modal opens for a different job. Resetting
  // runs first avoids briefly showing the previous job's rows while
  // the new fetch is in-flight.
  useEffect(() => {
    if (!open || !row) return;
    let alive = true;
    setRuns([]);
    setError(null);
    setLoading(true);
    listJobRuns(target, row.jobid, 50).then(r => {
      if (!alive) return;
      setLoading(false);
      if (!r.ok) {
        setError(r.error || 'Failed to load run history');
        return;
      }
      setRuns(r.rows ?? []);
    });
    return () => { alive = false; };
  }, [open, target, row?.jobid]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open || !row) return null;
  const label = row.function_name || row.jobname || `jobid ${row.jobid}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div
        className="w-full max-w-3xl rounded-2xl border border-overlay/[0.08] bg-surface-0 shadow-xl max-h-[85vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-overlay/[0.06] flex items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-surface-900">
              Run history — <span className="font-mono">{label}</span>
            </h3>
            <p className="text-[12px] text-surface-500 mt-0.5">
              Last 50 runs from <code>cron.job_run_details</code>. Ad-hoc "Run now" triggers do not appear here — they're recorded in the platform audit log instead.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-surface-500 hover:text-surface-700 p-1 -m-1 rounded-lg hover:bg-surface-100"
            title="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-auto p-5">
          {loading && (
            <div className="flex items-center justify-center py-10 text-surface-500 text-sm gap-2">
              <Loader2 size={14} className="animate-spin" /> Loading runs…
            </div>
          )}
          {error && !loading && (
            <div className="rounded-xl border border-red-500/20 bg-red-500/5 px-4 py-3 text-sm text-red-600 flex items-start gap-2">
              <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
          {!loading && !error && runs.length === 0 && (
            <div className="rounded-xl border border-dashed border-overlay/[0.12] bg-surface-50/50 px-4 py-6 text-center text-sm text-surface-500">
              No execution history yet. The cron may not have ticked since it was created, or run details were rotated out.
            </div>
          )}
          {!loading && !error && runs.length > 0 && (
            <div className="rounded-xl border border-overlay/[0.06] bg-surface-0 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-surface-50/60 text-surface-500 text-[11px] uppercase tracking-widest">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold">When</th>
                    <th className="text-left px-3 py-2 font-semibold">Duration</th>
                    <th className="text-left px-3 py-2 font-semibold">Status</th>
                    <th className="text-left px-3 py-2 font-semibold">Message</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map(run => {
                    const ok = run.status === 'succeeded';
                    const fail = run.status === 'failed';
                    return (
                      <tr key={run.runid} className="border-t border-overlay/[0.04] align-top">
                        <td className="px-3 py-2 whitespace-nowrap">
                          <div className="text-[12px] text-surface-700">{timeAgo(run.start_time)}</div>
                          {run.start_time && (
                            <div className="text-[10px] text-surface-500 mt-0.5" title={run.start_time}>
                              {new Date(run.start_time).toLocaleString()}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-[12px] text-surface-700">
                          {run.duration_ms == null
                            ? <span className="italic text-surface-400">—</span>
                            : run.duration_ms < 1000
                              ? `${run.duration_ms} ms`
                              : `${(run.duration_ms / 1000).toFixed(2)} s`}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {ok && (
                            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600">
                              <CheckCircle2 size={12} /> ok
                            </span>
                          )}
                          {fail && (
                            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-600">
                              <XCircle size={12} /> failed
                            </span>
                          )}
                          {!ok && !fail && (
                            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-surface-500">
                              <Clock size={12} /> {run.status || '?'}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {run.return_message ? (
                            <div
                              className={cn(
                                'text-[11px] font-mono break-all',
                                fail ? 'text-red-600' : 'text-surface-600',
                              )}
                            >
                              {run.return_message}
                            </div>
                          ) : (
                            <span className="text-[11px] italic text-surface-400">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Project selector ──────────────────────────────────────────────────────────

function ProjectSelector({
  target, onChange,
}: {
  target:   InfraTarget;
  onChange: (t: InfraTarget) => void;
}) {
  return (
    <div className="inline-flex p-1 rounded-xl bg-surface-200">
      {(['platform', 'app'] as InfraTarget[]).map(t => (
        <button
          key={t}
          onClick={() => onChange(t)}
          className={cn(
            'px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-widest transition-all',
            target === t
              ? 'bg-surface-0 text-surface-900 shadow-sm'
              : 'text-surface-500 hover:text-surface-700',
          )}
        >
          {t === 'platform' ? 'Platform DB' : 'App DB'}
        </button>
      ))}
    </div>
  );
}

// ── Scheduled Functions tab ───────────────────────────────────────────────────

function ScheduledFunctionsTab({ target }: { target: InfraTarget }) {
  const [rows, setRows]               = useState<InfraScheduledFunctionRow[]>([]);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState<string | null>(null);
  const [configured, setConfigured]   = useState<boolean>(true);
  const [confirm, setConfirm]         = useState<InfraScheduledFunctionRow | null>(null);
  const [submitting, setSubmitting]   = useState(false);
  // Per-row "Run now" in-flight indicator. Tracking by jobid lets the
  // operator click multiple "Run now" buttons in quick succession
  // without locking the whole tab.
  const [runningJobId, setRunningJobId] = useState<string | null>(null);
  // The job whose history modal is currently open. null = no modal.
  const [historyFor, setHistoryFor]   = useState<InfraScheduledFunctionRow | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const r = await listScheduledFunctions(target);
    if (!r.ok) {
      setError(r.error || 'Failed to list scheduled functions');
      if (r.configured === false) setConfigured(false);
      setRows([]);
    } else {
      setConfigured(true);
      setRows(r.rows ?? []);
    }
    setLoading(false);
  }, [target]);

  useEffect(() => { load(); }, [load]);

  const doToggle = async () => {
    if (!confirm) return;
    setSubmitting(true);
    const r = await toggleCronJob(target, confirm.jobid, !confirm.active);
    setSubmitting(false);
    if (!r.ok) {
      toast.error(r.error || 'Toggle failed');
      return;
    }
    toast.success(`${confirm.jobname || 'job'} ${!confirm.active ? 'enabled' : 'disabled'}`);
    setConfirm(null);
    load();
  };

  /** Trigger one cron'd function ad-hoc. The edge function looks up
   *  the job's command body and EXECUTEs it — same semantics as a
   *  real cron tick, but doesn't show up in cron.job_run_details
   *  (the underlying pg_net call is async, and we're outside cron's
   *  recording path). Operators verify success via the History modal
   *  a few seconds later, or via the underlying function's own logs. */
  const doRunNow = async (row: InfraScheduledFunctionRow) => {
    setRunningJobId(row.jobid);
    const r = await runCronNow(target, row.jobid, row.jobname);
    setRunningJobId(null);
    if (!r.ok) {
      toast.error(r.error || 'Run failed');
      return;
    }
    toast.success(`${row.function_name || row.jobname || 'job'} dispatched`, {
      description: 'The cron command was executed. Check History or function logs in a few seconds.',
    });
  };

  if (!configured) {
    return (
      <NotConfiguredCard
        title="Infrastructure tooling not configured on Platform"
        body={`Set SB_MGMT_TOKEN plus APP_SUPABASE_URL (or APP_SUPABASE_PROJECT_REF) on this project's edge functions, then redeploy infra-list + infra-mutate.`}
        error={error}
      />
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm text-surface-500">
          {loading ? 'Loading…' : `${rows.length} edge function${rows.length === 1 ? '' : 's'} on ${target === 'platform' ? 'Platform DB' : 'App DB'} cron`}
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-surface-100 hover:bg-surface-200 text-surface-700 transition-colors disabled:opacity-50"
        >
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="rounded-xl border border-red-500/20 bg-red-500/5 px-4 py-3 text-sm text-red-600 flex items-start gap-2">
          <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {rows.length === 0 && !loading && !error && (
        <div className="rounded-xl border border-dashed border-overlay/[0.12] bg-surface-50/50 px-4 py-6 text-center text-sm text-surface-500">
          No edge functions are scheduled via pg_cron on this project yet.
        </div>
      )}

      {rows.length > 0 && (
        <div className="rounded-xl border border-overlay/[0.06] bg-surface-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface-50/60 text-surface-500 text-[11px] uppercase tracking-widest">
              <tr>
                <th className="text-left px-4 py-2.5 font-semibold">Function</th>
                <th className="text-left px-4 py-2.5 font-semibold">Schedule</th>
                <th className="text-left px-4 py-2.5 font-semibold">Last run</th>
                <th className="text-left px-4 py-2.5 font-semibold">Status</th>
                <th className="text-right px-4 py-2.5 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const refs = detectSecretRefs(r.command);
                return (
                  <tr key={r.jobid} className="border-t border-overlay/[0.04] align-top">
                    <td className="px-4 py-3">
                      <div className="font-mono text-sm font-semibold text-surface-900">
                        {r.function_name || <span className="italic text-surface-500">(unparsed)</span>}
                      </div>
                      <div className="text-[11px] text-surface-500 mt-0.5">
                        {r.jobname || `jobid ${r.jobid}`}
                      </div>
                      {refs.length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {refs.map(n => (
                            <span
                              key={n}
                              title="Secret reference auto-detected from command body"
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono bg-surface-100 text-surface-700 border border-overlay/[0.06]"
                            >
                              <KeyRound size={9} />
                              {n}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-mono text-[12px] text-surface-700">{r.schedule}</div>
                      <div className="text-[11px] text-surface-500 mt-0.5">{humanCron(r.schedule)}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-[12px] text-surface-700">{timeAgo(r.last_run_at)}</div>
                      {r.last_run_at && (
                        <div className="text-[10px] text-surface-500 mt-0.5" title={r.last_run_at}>
                          {new Date(r.last_run_at).toLocaleString()}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {r.last_status === 'succeeded' && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600">
                          <CheckCircle2 size={12} /> ok
                        </span>
                      )}
                      {r.last_status === 'failed' && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-600" title={r.last_message ?? ''}>
                          <XCircle size={12} /> failed
                        </span>
                      )}
                      {!r.last_status && (
                        <span className="inline-flex items-center gap-1 text-[11px] text-surface-400">
                          <Clock size={12} /> never run
                        </span>
                      )}
                      {r.last_status === 'failed' && r.last_message && (
                        <div className="text-[10px] text-red-500 mt-1 max-w-[18rem] truncate" title={r.last_message}>
                          {r.last_message}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {/* Three stacked actions: Run now (ad-hoc trigger),
                          History (last 50 runs modal), and the Active
                          toggle. Right-aligned + wraps on narrow screens.
                          Run is disabled while its own job is dispatching;
                          other rows stay clickable. */}
                      <div className="flex items-center justify-end gap-1.5 flex-wrap">
                        <button
                          onClick={() => doRunNow(r)}
                          disabled={runningJobId === r.jobid}
                          title="Trigger this function now (bypasses schedule). Won't show in History — check function logs."
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-semibold text-surface-700 bg-surface-100 hover:bg-surface-200 transition-colors disabled:opacity-50"
                        >
                          {runningJobId === r.jobid
                            ? <Loader2 size={11} className="animate-spin" />
                            : <Play size={11} />}
                          Run
                        </button>
                        <button
                          onClick={() => setHistoryFor(r)}
                          title="View last 50 cron runs for this job"
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-semibold text-surface-700 bg-surface-100 hover:bg-surface-200 transition-colors"
                        >
                          <History size={11} />
                          History
                        </button>
                        <button
                          onClick={() => setConfirm(r)}
                          title={r.active ? 'Disable this cron' : 'Enable this cron'}
                          className={cn(
                            'inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-bold uppercase tracking-widest transition-colors',
                            r.active
                              ? 'bg-emerald-500/10 text-emerald-700 hover:bg-emerald-500/20'
                              : 'bg-surface-200 text-surface-600 hover:bg-surface-300',
                          )}
                        >
                          <Power size={11} />
                          {r.active ? 'On' : 'Off'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={!!confirm}
        onClose={() => !submitting && setConfirm(null)}
        title={confirm?.active ? 'Disable scheduled function?' : 'Enable scheduled function?'}
      >
        {confirm && (
          <div className="space-y-3">
            <p className="text-sm text-surface-700">
              {confirm.active ? (
                <>This will <strong>pause</strong> <span className="font-mono">{confirm.function_name || confirm.jobname}</span>. The job will stop running until you re-enable it.</>
              ) : (
                <>This will <strong>resume</strong> <span className="font-mono">{confirm.function_name || confirm.jobname}</span> on its existing schedule ({humanCron(confirm.schedule)}).</>
              )}
            </p>
            {(confirm.function_name === 'live-trip-tick' || confirm.function_name === 'sync-usage-metrics') && confirm.active && (
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-[12px] text-amber-700 flex items-start gap-2">
                <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
                <span>This job is load-bearing. Pausing it will stop production behaviour (push notifications / usage rollups). Re-enable as soon as the maintenance window ends.</span>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setConfirm(null)}
                disabled={submitting}
                className="px-3 py-1.5 rounded-lg text-sm font-medium text-surface-600 hover:bg-surface-100"
              >
                Cancel
              </button>
              <button
                onClick={doToggle}
                disabled={submitting}
                className={cn(
                  'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors',
                  confirm.active
                    ? 'bg-amber-500 text-white hover:bg-amber-600'
                    : 'bg-emerald-500 text-white hover:bg-emerald-600',
                )}
              >
                {submitting && <Loader2 size={14} className="animate-spin" />}
                {confirm.active ? 'Disable' : 'Enable'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      <JobRunsModal
        open={!!historyFor}
        onClose={() => setHistoryFor(null)}
        target={target}
        row={historyFor}
      />
    </div>
  );
}

// ── Secrets tab ──────────────────────────────────────────────────────────────

function SecretsTab({ target }: { target: InfraTarget }) {
  const [vault, setVault]             = useState<InfraVaultSecretRow[]>([]);
  const [edge, setEdge]               = useState<InfraEdgeSecretRow[]>([]);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState<string | null>(null);
  const [configured, setConfigured]   = useState(true);
  const [showNew, setShowNew]         = useState(false);
  const [rotateRow, setRotateRow]     = useState<InfraVaultSecretRow | null>(null);
  const [unsetRow, setUnsetRow]       = useState<InfraVaultSecretRow | null>(null);
  // Edge-function secret editing.
  const [showNewEdge, setShowNewEdge] = useState(false);
  const [setEdgeRow, setSetEdgeRow]   = useState<InfraEdgeSecretRow | null>(null);
  const [unsetEdgeRow, setUnsetEdgeRow] = useState<InfraEdgeSecretRow | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const [v, e] = await Promise.all([listVaultSecrets(target), listEdgeSecrets(target)]);
    if (!v.ok) {
      setError(v.error || 'Failed to list Vault secrets');
      if (v.configured === false) setConfigured(false);
    } else {
      setConfigured(true);
      setVault(v.rows ?? []);
    }
    if (!e.ok) {
      // Don't override a Vault-side error; surface edge-side failure
      // separately if relevant.
      if (!error) setError(prev => prev ?? (e.error || 'Failed to list edge secrets'));
    } else {
      setEdge(e.rows ?? []);
    }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  useEffect(() => { load(); }, [load]);

  if (!configured) {
    return (
      <NotConfiguredCard
        title="Infrastructure tooling not configured on Platform"
        body={`Set SB_MGMT_TOKEN plus APP_SUPABASE_URL (or APP_SUPABASE_PROJECT_REF) on this project's edge functions, then redeploy infra-list + infra-mutate.`}
        error={error}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* ── Vault secrets ── */}
      <section className="rounded-xl border border-overlay/[0.06] bg-surface-0 overflow-hidden">
        <header className="flex items-center justify-between px-4 py-3 border-b border-overlay/[0.06]">
          <div>
            <h3 className="text-sm font-semibold text-surface-900 flex items-center gap-2">
              <KeyRound size={14} className="text-brand-500" />
              Vault secrets
            </h3>
            <p className="text-[11px] text-surface-500 mt-0.5">Encrypted by the supabase_vault extension. Used by pg_cron + triggers.</p>
          </div>
          <button
            onClick={() => setShowNew(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-brand-500 text-white hover:bg-brand-600 transition-colors"
          >
            <Plus size={12} />
            New
          </button>
        </header>
        {loading && vault.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-surface-500">
            <Loader2 size={16} className="animate-spin inline mr-2" />
            Loading…
          </div>
        ) : vault.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-surface-500">No Vault secrets seeded on this project.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-surface-50/60 text-surface-500 text-[11px] uppercase tracking-widest">
              <tr>
                <th className="text-left px-4 py-2.5 font-semibold">Name</th>
                <th className="text-left px-4 py-2.5 font-semibold">Description</th>
                <th className="text-left px-4 py-2.5 font-semibold">Created</th>
                <th className="text-left px-4 py-2.5 font-semibold">Last rotated</th>
                <th className="text-right px-4 py-2.5 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {vault.map(r => (
                <tr key={r.id} className="border-t border-overlay/[0.04]">
                  <td className="px-4 py-3 font-mono text-[12px] text-surface-900 font-semibold">{r.name}</td>
                  <td className="px-4 py-3 text-[12px] text-surface-600 max-w-[20rem]">{r.description || <span className="italic text-surface-400">—</span>}</td>
                  <td className="px-4 py-3 text-[12px] text-surface-600" title={r.created_at ?? ''}>{timeAgo(r.created_at)}</td>
                  <td className="px-4 py-3 text-[12px] text-surface-600" title={r.updated_at ?? ''}>
                    {r.updated_at && r.updated_at !== r.created_at ? timeAgo(r.updated_at) : <span className="text-surface-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        onClick={() => setRotateRow(r)}
                        title="Rotate (replace value in-place)"
                        className="p-1.5 rounded-lg hover:bg-surface-100 text-surface-600 hover:text-surface-900"
                      >
                        <RefreshCw size={14} />
                      </button>
                      <button
                        onClick={() => setUnsetRow(r)}
                        title="Unset (drop from Vault)"
                        className="p-1.5 rounded-lg hover:bg-red-500/10 text-red-500 hover:text-red-600"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* ── Edge function secrets (editable) ── */}
      <section className="rounded-xl border border-overlay/[0.06] bg-surface-0 overflow-hidden">
        <header className="flex items-center justify-between px-4 py-3 border-b border-overlay/[0.06]">
          <div>
            <h3 className="text-sm font-semibold text-surface-900 flex items-center gap-2">
              <Server size={14} className="text-brand-500" />
              Edge function environment secrets
            </h3>
            <p className="text-[11px] text-surface-500 mt-0.5">Integration API keys (Stripe, Duffel, OpenAI…). Values are write-only — never shown; set a new value to change one. Saving restarts this project's edge functions (brief propagation).</p>
          </div>
          <button
            onClick={() => setShowNewEdge(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-brand-500 text-white hover:bg-brand-600 transition-colors shrink-0"
          >
            <Plus size={12} />
            New
          </button>
        </header>
        {loading && edge.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-surface-500">
            <Loader2 size={16} className="animate-spin inline mr-2" />
            Loading…
          </div>
        ) : edge.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-surface-500">No edge-function secrets visible on this project.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-surface-50/60 text-surface-500 text-[11px] uppercase tracking-widest">
              <tr>
                <th className="text-left px-4 py-2.5 font-semibold">Name</th>
                <th className="text-left px-4 py-2.5 font-semibold">Updated</th>
                <th className="text-right px-4 py-2.5 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {edge.map(r => (
                <tr key={r.name} className="border-t border-overlay/[0.04]">
                  <td className="px-4 py-3 font-mono text-[12px] text-surface-900">{r.name}</td>
                  <td className="px-4 py-3 text-[12px] text-surface-600" title={r.updated_at ?? ''}>{timeAgo(r.updated_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        onClick={() => setSetEdgeRow(r)}
                        title="Set / update value"
                        className="p-1.5 rounded-lg hover:bg-surface-100 text-surface-600 hover:text-surface-900"
                      >
                        <RefreshCw size={14} />
                      </button>
                      <button
                        onClick={() => setUnsetEdgeRow(r)}
                        title="Unset (delete from edge secrets)"
                        className="p-1.5 rounded-lg hover:bg-red-500/10 text-red-500 hover:text-red-600"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <NewVaultSecretModal
        open={showNew}
        target={target}
        onClose={() => setShowNew(false)}
        onSaved={() => { setShowNew(false); load(); }}
      />
      <RotateVaultSecretModal
        row={rotateRow}
        target={target}
        onClose={() => setRotateRow(null)}
        onSaved={() => { setRotateRow(null); load(); }}
      />
      <UnsetVaultSecretModal
        row={unsetRow}
        target={target}
        onClose={() => setUnsetRow(null)}
        onSaved={() => { setUnsetRow(null); load(); }}
      />

      <NewEdgeSecretModal
        open={showNewEdge}
        target={target}
        onClose={() => setShowNewEdge(false)}
        onSaved={() => { setShowNewEdge(false); load(); }}
      />
      <SetEdgeSecretModal
        row={setEdgeRow}
        target={target}
        onClose={() => setSetEdgeRow(null)}
        onSaved={() => { setSetEdgeRow(null); load(); }}
      />
      <UnsetEdgeSecretModal
        row={unsetEdgeRow}
        target={target}
        onClose={() => setUnsetEdgeRow(null)}
        onSaved={() => { setUnsetEdgeRow(null); load(); }}
      />
    </div>
  );
}

// ── Vault secret modals ───────────────────────────────────────────────────────

function VaultValueInput({
  value, onChange, autoFocus,
}: {
  value: string;
  onChange: (v: string) => void;
  autoFocus?: boolean;
}) {
  const [reveal, setReveal] = useState(false);
  return (
    <div className="relative">
      <input
        type={reveal ? 'text' : 'password'}
        value={value}
        autoFocus={autoFocus}
        onChange={(e) => onChange(e.target.value)}
        className="w-full pr-10 px-3 py-2 rounded-lg border border-overlay/[0.08] bg-surface-50 text-surface-900 font-mono text-[12px] focus:outline-none focus:border-brand-500"
        placeholder="paste the secret value"
      />
      <button
        type="button"
        onClick={() => setReveal(v => !v)}
        title={reveal ? 'Hide' : 'Reveal'}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-surface-500 hover:text-surface-800"
      >
        {reveal ? <EyeOff size={14} /> : <Eye size={14} />}
      </button>
    </div>
  );
}

function NewVaultSecretModal({
  open, target, onClose, onSaved,
}: {
  open: boolean;
  target: InfraTarget;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName]               = useState('');
  const [value, setValue]             = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy]               = useState(false);

  useEffect(() => {
    if (!open) { setName(''); setValue(''); setDescription(''); }
  }, [open]);

  const submit = async () => {
    if (!name || !value) {
      toast.error('Name and value are required');
      return;
    }
    setBusy(true);
    const r = await createVaultSecret(target, name.trim(), value, description.trim() || undefined);
    setBusy(false);
    if (!r.ok) { toast.error(r.error || 'Create failed'); return; }
    toast.success(`Vault secret "${name}" created on ${target === 'platform' ? 'Platform DB' : 'App DB'}`);
    onSaved();
  };

  return (
    <Modal open={open} onClose={() => !busy && onClose()} title={`New Vault secret · ${target === 'platform' ? 'Platform DB' : 'App DB'}`}>
      <div className="space-y-3">
        <label className="block text-xs font-semibold text-surface-700">
          Name
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 w-full px-3 py-2 rounded-lg border border-overlay/[0.08] bg-surface-50 text-surface-900 font-mono text-[12px] focus:outline-none focus:border-brand-500"
            placeholder="e.g. app_provision_secret"
            autoFocus
          />
          <span className="block text-[10px] text-surface-500 mt-1">Letters, digits, underscores. Max 64 chars.</span>
        </label>
        <label className="block text-xs font-semibold text-surface-700">
          Value
          <div className="mt-1">
            <VaultValueInput value={value} onChange={setValue} />
          </div>
        </label>
        <label className="block text-xs font-semibold text-surface-700">
          Description <span className="text-surface-400 font-normal">(optional)</span>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="mt-1 w-full px-3 py-2 rounded-lg border border-overlay/[0.08] bg-surface-50 text-surface-900 text-[12px] focus:outline-none focus:border-brand-500"
            placeholder="What is this secret used for?"
          />
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} disabled={busy} className="px-3 py-1.5 rounded-lg text-sm font-medium text-surface-600 hover:bg-surface-100">Cancel</button>
          <button onClick={submit} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-brand-500 text-white hover:bg-brand-600">
            {busy && <Loader2 size={14} className="animate-spin" />}
            Create
          </button>
        </div>
      </div>
    </Modal>
  );
}

function RotateVaultSecretModal({
  row, target, onClose, onSaved,
}: {
  row: InfraVaultSecretRow | null;
  target: InfraTarget;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [value, setValue] = useState('');
  const [busy, setBusy]   = useState(false);

  useEffect(() => { if (!row) setValue(''); }, [row]);

  const submit = async () => {
    if (!row || !value) return;
    setBusy(true);
    const r = await rotateVaultSecret(target, row.name, value);
    setBusy(false);
    if (!r.ok) { toast.error(r.error || 'Rotate failed'); return; }
    toast.success(`Rotated "${row.name}"`);
    onSaved();
  };

  return (
    <Modal open={!!row} onClose={() => !busy && onClose()} title={row ? `Rotate · ${row.name}` : ''}>
      {row && (
        <div className="space-y-3">
          <p className="text-sm text-surface-700">
            Replace the current value of <span className="font-mono font-semibold">{row.name}</span>. The previous value is encrypted and can't be retrieved — once you save here, the old value is gone.
          </p>
          <label className="block text-xs font-semibold text-surface-700">
            New value
            <div className="mt-1">
              <VaultValueInput value={value} onChange={setValue} autoFocus />
            </div>
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} disabled={busy} className="px-3 py-1.5 rounded-lg text-sm font-medium text-surface-600 hover:bg-surface-100">Cancel</button>
            <button onClick={submit} disabled={busy || !value} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50">
              {busy && <Loader2 size={14} className="animate-spin" />}
              Rotate
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function UnsetVaultSecretModal({
  row, target, onClose, onSaved,
}: {
  row: InfraVaultSecretRow | null;
  target: InfraTarget;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [typed, setTyped] = useState('');
  useEffect(() => { if (!row) setTyped(''); }, [row]);

  const submit = async () => {
    if (!row) return;
    setBusy(true);
    const r = await unsetVaultSecret(target, row.name);
    setBusy(false);
    if (!r.ok) { toast.error(r.error || 'Unset failed'); return; }
    toast.success(`Dropped "${row.name}"`);
    onSaved();
  };

  return (
    <Modal open={!!row} onClose={() => !busy && onClose()} title={row ? `Unset · ${row.name}` : ''}>
      {row && (
        <div className="space-y-3">
          <div className="rounded-lg border border-red-500/30 bg-red-500/5 px-3 py-2 text-[12px] text-red-700 flex items-start gap-2">
            <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
            <span>This will <strong>drop</strong> the secret. Any pg_cron job or trigger that reads it will start failing on the next tick.</span>
          </div>
          <label className="block text-xs font-semibold text-surface-700">
            Type the secret name to confirm
            <input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoFocus
              className="mt-1 w-full px-3 py-2 rounded-lg border border-overlay/[0.08] bg-surface-50 text-surface-900 font-mono text-[12px] focus:outline-none focus:border-red-500"
              placeholder={row.name}
            />
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} disabled={busy} className="px-3 py-1.5 rounded-lg text-sm font-medium text-surface-600 hover:bg-surface-100">Cancel</button>
            <button
              onClick={submit}
              disabled={busy || typed !== row.name}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-red-500 text-white hover:bg-red-600 disabled:opacity-40"
            >
              {busy && <Loader2 size={14} className="animate-spin" />}
              Unset
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ── Edge-function secret modals ────────────────────────────────────────────────

/** Loud reminder shown in every edge-secret write modal — these restart the
 *  project's functions and can touch live payment keys. */
function EdgeSecretWarning() {
  return (
    <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-[11px] text-amber-700 flex items-start gap-2">
      <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" />
      <span>Saving restarts this project's edge functions (brief propagation). These are live integration keys — double-check the value before saving.</span>
    </div>
  );
}

function NewEdgeSecretModal({
  open, target, onClose, onSaved,
}: {
  open: boolean;
  target: InfraTarget;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName]   = useState('');
  const [value, setValue] = useState('');
  const [busy, setBusy]   = useState(false);

  useEffect(() => { if (!open) { setName(''); setValue(''); } }, [open]);

  const submit = async () => {
    if (!name || !value) { toast.error('Name and value are required'); return; }
    setBusy(true);
    const r = await setEdgeSecret(target, name.trim(), value);
    setBusy(false);
    if (!r.ok) { toast.error(r.error || 'Set failed'); return; }
    toast.success(`Set "${name}" on ${target === 'platform' ? 'Platform' : 'App'} edge functions`);
    onSaved();
  };

  return (
    <Modal open={open} onClose={() => !busy && onClose()} title={`New edge secret · ${target === 'platform' ? 'Platform' : 'App'}`}>
      <div className="space-y-3">
        <EdgeSecretWarning />
        <label className="block text-xs font-semibold text-surface-700">
          Name
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 w-full px-3 py-2 rounded-lg border border-overlay/[0.08] bg-surface-50 text-surface-900 font-mono text-[12px] focus:outline-none focus:border-brand-500"
            placeholder="e.g. DUFFEL_API_KEY"
            autoFocus
          />
          <span className="block text-[10px] text-surface-500 mt-1">Letters, digits, underscores. Cannot start with SUPABASE_.</span>
        </label>
        <label className="block text-xs font-semibold text-surface-700">
          Value
          <div className="mt-1"><VaultValueInput value={value} onChange={setValue} /></div>
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} disabled={busy} className="px-3 py-1.5 rounded-lg text-sm font-medium text-surface-600 hover:bg-surface-100">Cancel</button>
          <button onClick={submit} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-brand-500 text-white hover:bg-brand-600">
            {busy && <Loader2 size={14} className="animate-spin" />}
            Set
          </button>
        </div>
      </div>
    </Modal>
  );
}

function SetEdgeSecretModal({
  row, target, onClose, onSaved,
}: {
  row: InfraEdgeSecretRow | null;
  target: InfraTarget;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [value, setValue] = useState('');
  const [busy, setBusy]   = useState(false);
  useEffect(() => { if (!row) setValue(''); }, [row]);

  const submit = async () => {
    if (!row || !value) return;
    setBusy(true);
    const r = await setEdgeSecret(target, row.name, value);
    setBusy(false);
    if (!r.ok) { toast.error(r.error || 'Set failed'); return; }
    toast.success(`Updated "${row.name}"`);
    onSaved();
  };

  return (
    <Modal open={!!row} onClose={() => !busy && onClose()} title={row ? `Set value · ${row.name}` : ''}>
      {row && (
        <div className="space-y-3">
          <EdgeSecretWarning />
          <p className="text-sm text-surface-700">
            Replace the value of <span className="font-mono font-semibold">{row.name}</span>. The current value isn't shown (write-only); typing a new one overwrites it.
          </p>
          <label className="block text-xs font-semibold text-surface-700">
            New value
            <div className="mt-1"><VaultValueInput value={value} onChange={setValue} autoFocus /></div>
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} disabled={busy} className="px-3 py-1.5 rounded-lg text-sm font-medium text-surface-600 hover:bg-surface-100">Cancel</button>
            <button onClick={submit} disabled={busy || !value} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50">
              {busy && <Loader2 size={14} className="animate-spin" />}
              Save
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function UnsetEdgeSecretModal({
  row, target, onClose, onSaved,
}: {
  row: InfraEdgeSecretRow | null;
  target: InfraTarget;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [busy, setBusy]   = useState(false);
  const [typed, setTyped] = useState('');
  useEffect(() => { if (!row) setTyped(''); }, [row]);

  const submit = async () => {
    if (!row) return;
    setBusy(true);
    const r = await unsetEdgeSecret(target, row.name);
    setBusy(false);
    if (!r.ok) { toast.error(r.error || 'Unset failed'); return; }
    toast.success(`Dropped "${row.name}"`);
    onSaved();
  };

  return (
    <Modal open={!!row} onClose={() => !busy && onClose()} title={row ? `Unset · ${row.name}` : ''}>
      {row && (
        <div className="space-y-3">
          <div className="rounded-lg border border-red-500/30 bg-red-500/5 px-3 py-2 text-[12px] text-red-700 flex items-start gap-2">
            <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
            <span>This <strong>deletes</strong> the secret from the edge functions. Any function that reads it will start failing after the restart.</span>
          </div>
          <label className="block text-xs font-semibold text-surface-700">
            Type the secret name to confirm
            <input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoFocus
              className="mt-1 w-full px-3 py-2 rounded-lg border border-overlay/[0.08] bg-surface-50 text-surface-900 font-mono text-[12px] focus:outline-none focus:border-red-500"
              placeholder={row.name}
            />
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} disabled={busy} className="px-3 py-1.5 rounded-lg text-sm font-medium text-surface-600 hover:bg-surface-100">Cancel</button>
            <button onClick={submit} disabled={busy || typed !== row.name} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-red-500 text-white hover:bg-red-600 disabled:opacity-40">
              {busy && <Loader2 size={14} className="animate-spin" />}
              Unset
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ── Audit log tab ─────────────────────────────────────────────────────────────

/** Pull a stringy field out of an unknown payload — audit rows store
 *  JSONB so the client sees a Record<string, unknown>. Returns empty
 *  string if the key is missing or not a string. */
function payloadStr(p: Record<string, unknown> | null, key: string): string {
  const v = p?.[key];
  return typeof v === 'string' ? v : '';
}

/** Friendly inline summary for an audit row. Branches on `action` so the
 *  operator immediately sees "tenant X · story Y" without having to
 *  expand the row. Falls back to the raw target_name for unknown
 *  actions so we never lose the original field. The IDs are kept on
 *  separate, smaller, mono lines so they're copyable but don't
 *  dominate visually. */
function AuditTargetSummary({ row }: { row: InfraAuditRow }) {
  const p = row.payload;
  const a = row.action;

  // Journal moderation — Stories Phase 2 + 3A. payload carries:
  // tenant_id, tenant_label, journal_id, story_title, reason
  // For journal_reports_dismissed the payload also carries
  // dismissed_count + dismissed_reason — same rendering pattern.
  if (a === 'journal_hide' || a === 'journal_unhide' || a === 'journal_reports_dismissed') {
    const tenantLabel = payloadStr(p, 'tenant_label') || '(tenant)';
    const tenantId    = payloadStr(p, 'tenant_id');
    const journalId   = payloadStr(p, 'journal_id') || row.target_name;
    const storyTitle  = payloadStr(p, 'story_title') || '(untitled story)';
    const reason      = payloadStr(p, 'reason');
    return (
      <div className="space-y-0.5">
        <div className="text-[12px] text-surface-900">
          <span className="font-semibold">{tenantLabel}</span>
          <span className="text-surface-400"> · </span>
          <span>{storyTitle}</span>
        </div>
        <div className="text-[10px] font-mono text-surface-500 break-all">
          tenant <span className="text-surface-600">{tenantId || '—'}</span>
          {' · '}
          story <span className="text-surface-600">{journalId}</span>
        </div>
        {reason && (
          <div className="text-[10px] text-surface-500 italic">
            reason: {reason}
          </div>
        )}
      </div>
    );
  }

  // Impersonation — start row from start-impersonation writes target
  // tenant + user info into payload; end row from
  // record-impersonation-end mirrors that shape. target_name on these
  // rows is "<tenant_name> / <target_email>".
  if (a === 'impersonation_started' || a === 'impersonation_failed' || a === 'impersonation_ended') {
    const tenantId   = payloadStr(p, 'target_tenant_id');
    const userId     = payloadStr(p, 'target_user_id');
    const targetEml  = payloadStr(p, 'target_email');
    const tenantName = payloadStr(p, 'tenant_name'); // may be missing on legacy rows
    const reason     = payloadStr(p, 'reason');
    // Fall back to target_name's "Tenant / email" split if we don't
    // have the structured tenant_name field on legacy rows.
    const fallbackTenantName = !tenantName && row.target_name.includes(' / ')
      ? row.target_name.split(' / ')[0]
      : tenantName;
    return (
      <div className="space-y-0.5">
        <div className="text-[12px] text-surface-900">
          <span className="font-semibold">{fallbackTenantName || '(tenant)'}</span>
          <span className="text-surface-400"> · </span>
          <span className="font-mono">{targetEml || row.target_name}</span>
        </div>
        <div className="text-[10px] font-mono text-surface-500 break-all">
          tenant <span className="text-surface-600">{tenantId || '—'}</span>
          {' · '}
          user <span className="text-surface-600">{userId || '—'}</span>
        </div>
        {reason && (
          <div className="text-[10px] text-surface-500 italic">
            reason: {reason}
          </div>
        )}
      </div>
    );
  }

  // Default — the historical "<project> ▸ <name>" shape, kept exactly
  // for cron / vault / secret rows so the existing display doesn't
  // regress.
  return (
    <span className="font-mono text-[12px] text-surface-700">
      <span className="text-surface-400">{row.target_project}</span>
      <ChevronRight size={11} className="inline mx-0.5 text-surface-400" />
      {row.target_name}
    </span>
  );
}

function AuditLogTab() {
  const [rows, setRows] = useState<InfraAuditRow[]>([]);
  const [loading, setLoading] = useState(false);
  // Track which row's payload is expanded — at most one at a time so the
  // table never grows unbounded vertically.
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetchInfraAuditLog(100);
      setRows(r);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm text-surface-500">{loading ? 'Loading…' : `${rows.length} recent event${rows.length === 1 ? '' : 's'}`}</div>
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-surface-100 hover:bg-surface-200 text-surface-700"
        >
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>
      {rows.length === 0 && !loading ? (
        <div className="rounded-xl border border-dashed border-overlay/[0.12] bg-surface-50/50 px-4 py-6 text-center text-sm text-surface-500">
          No infrastructure mutations yet.
        </div>
      ) : (
        <div className="rounded-xl border border-overlay/[0.06] bg-surface-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface-50/60 text-surface-500 text-[11px] uppercase tracking-widest">
              <tr>
                <th className="text-left px-4 py-2.5 font-semibold w-6"></th>
                <th className="text-left px-4 py-2.5 font-semibold">When</th>
                <th className="text-left px-4 py-2.5 font-semibold">Actor</th>
                <th className="text-left px-4 py-2.5 font-semibold">Action</th>
                <th className="text-left px-4 py-2.5 font-semibold">Target</th>
                <th className="text-left px-4 py-2.5 font-semibold">Result</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const expanded = expandedId === r.id;
                return (
                  <Fragment key={r.id}>
                    <tr
                      className="border-t border-overlay/[0.04] hover:bg-surface-50/50 cursor-pointer"
                      onClick={() => setExpandedId(expanded ? null : r.id)}
                    >
                      <td className="px-4 py-2.5 text-surface-400">
                        <ChevronRight
                          size={12}
                          className={`transition-transform ${expanded ? 'rotate-90' : ''}`}
                        />
                      </td>
                      <td className="px-4 py-2.5 text-[12px] text-surface-600 align-top" title={r.created_at}>{timeAgo(r.created_at)}</td>
                      <td className="px-4 py-2.5 text-[12px] text-surface-700 align-top">{r.actor_email}</td>
                      <td className="px-4 py-2.5 text-[12px] font-mono text-surface-900 align-top">{r.action}</td>
                      <td className="px-4 py-2.5 align-top">
                        <AuditTargetSummary row={r} />
                      </td>
                      <td className="px-4 py-2.5 align-top">
                        {r.ok ? (
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600">
                            <CheckCircle2 size={11} /> ok
                          </span>
                        ) : (
                          <span title={r.error_message ?? ''} className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-600">
                            <XCircle size={11} /> failed
                          </span>
                        )}
                      </td>
                    </tr>
                    {/* Expanded payload — full JSON for the operator who
                        wants to inspect everything. Pretty-printed with
                        2-space indent so it's scannable. */}
                    {expanded && (
                      <tr className="border-t border-overlay/[0.04] bg-surface-50/40">
                        <td colSpan={6} className="px-4 py-3">
                          <div className="text-[10px] uppercase tracking-wider text-surface-500 mb-1">
                            Full payload
                          </div>
                          <pre className="text-[11px] font-mono text-surface-700 bg-surface-0 border border-surface-200 rounded-md p-2 overflow-x-auto max-h-60">
{JSON.stringify(r.payload ?? {}, null, 2)}
                          </pre>
                          {r.error_message && (
                            <>
                              <div className="text-[10px] uppercase tracking-wider text-rose-500 mt-2 mb-1">
                                Error
                              </div>
                              <pre className="text-[11px] font-mono text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-2 overflow-x-auto">
{r.error_message}
                              </pre>
                            </>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Misc helpers ──────────────────────────────────────────────────────────────

function NotConfiguredCard({ title, body, error }: { title: string; body: string; error: string | null }) {
  return (
    <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 px-4 py-4 text-sm text-amber-700 flex items-start gap-3">
      <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
      <div className="space-y-1">
        <div className="font-semibold">{title}</div>
        <div className="text-[12px] text-amber-700/90">{body}</div>
        {error && <div className="text-[11px] text-amber-700/70 mt-1 font-mono">{error}</div>}
      </div>
    </div>
  );
}

// ── Page shell ────────────────────────────────────────────────────────────────

type Tab = 'catalog' | 'cron' | 'secrets' | 'audit';

export function InfrastructurePage() {
  const [target, setTarget] = useState<InfraTarget>('app');
  // Default to the catalog — operators tend to ask "what is this?" before
  // "what's it doing right now?". Catalog is read-only so loading it first
  // also avoids a network round-trip on first paint.
  const [tab, setTab]       = useState<Tab>('catalog');

  const tabs = useMemo<Array<{ id: Tab; label: string; icon: any }>>(() => [
    { id: 'catalog', label: 'Catalog',             icon: Zap },
    { id: 'cron',    label: 'Scheduled Functions', icon: Database },
    { id: 'secrets', label: 'Secrets',             icon: KeyRound },
    { id: 'audit',   label: 'Audit Log',           icon: ScrollText },
  ], []);

  // The project selector only makes sense for tabs that operate on ONE
  // project at a time (cron + secrets). The catalog spans both projects
  // by design; audit log shows entries from any target.
  const showProjectSelector = tab === 'cron' || tab === 'secrets';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-surface-900 flex items-center gap-2">
            <Server size={22} className="text-brand-500" />
            Infrastructure
          </h1>
          <p className="text-surface-500 mt-1 text-sm">
            Server-side catalog, edge-function crons, Vault secrets, and audit log — across both Platform &amp; App projects.
          </p>
        </div>
        {showProjectSelector && <ProjectSelector target={target} onChange={setTarget} />}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-surface-200 rounded-xl w-fit">
        {tabs.map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all',
                tab === t.id
                  ? 'bg-surface-0 text-surface-900 shadow-sm'
                  : 'text-surface-500 hover:text-surface-700',
              )}
            >
              <Icon size={14} />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Body */}
      {tab === 'catalog' && <EdgeFunctionsCatalogTab />}
      {tab === 'cron'    && <ScheduledFunctionsTab target={target} />}
      {tab === 'secrets' && <SecretsTab target={target} />}
      {tab === 'audit'   && <AuditLogTab />}
    </div>
  );
}

export default InfrastructurePage;
